<template>
  <Loader
    :value="promises.fetchOne"
    class="project-mutate view"
    :data-project-theme="$path('theme', item)"
    :message="$metaInfo.title"
    :key="id"
  >
    <div class="container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>
            <img
              v-if="($store.state.enums.UsedProjectLanguages || []).length > 1 && item.metadata"
              class="ml-2"
              :src="`/images/flags/${item.metadata.language}.svg`"
              width="20"
            />
          </div>
        </template>
        <template #center>
          <transition name="fade">
            <DraftAlert v-if="hasActiveDraft" />
          </transition>
        </template>
        <template #actions>
          <Preview :item="item" />

          <Actions
            placement="project"
            :query="{ project: id, 'kvass-draft': draft || showDraft }"
            :items="customActions"
          />

          <ButtonComponent
            v-if="!showDraft"
            :label="saveState.save.label"
            :success-label="$t('saved')"
            :loading-label="$t('saving')"
            :icon="['fal', saveState.save.icon]"
            :theme="saveState.save.theme"
            :promise="promises.mutate"
            :class="{
              'elder-button--disabled': saveState.save.disabled,
              'elder-button--invalid': saveState.save.invalid,
            }"
            v-tooltip="{ content: saveState.save.tooltip }"
            @click="submit"
          >
          </ButtonComponent>

          <Draft
            v-else
            v-model="draft"
            :saveState="saveState"
            :reference="{ ref: id, onModel: 'Project' }"
            :submit="submit"
            @reset="() => fetch({ id: id, draft: draft })"
            @input="
              draftId => {
                item.draftId = draftId
                key += 1
              }
            "
          />
        </template>
      </HeaderComponent>

      <StepperComponent
        v-if="hasActiveStep"
        :key="key"
        :value="steps"
        ref="stepper"
        :stateKey="stateKey"
        state-handler="route"
        transition="fade"
        @submit="submit"
      >
        <template #breadcrumb="{ item, navigate, index }">
          <button
            type="button"
            class="elder-stepper__breadcrumb-button"
            :class="{ 'elder-stepper__breadcrumb-button--invalid': !states.fetchOne && !item.isValid }"
            @click="navigate"
            :disabled="item.disabled"
          >
            <span class="elder-stepper__breadcrumb-icon">
              <template v-if="states.fetchOne || item.isValid">{{ index + 1 }}</template>
              <FontAwesomeIcon v-else :icon="['fas', 'exclamation-triangle']" size="sm" />
            </span>
            <span>{{ item.label }}</span>
          </button>
        </template>
        <template #after="{ meta, next, prev }">
          <div class="project-mutate__navigation">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              type="submit"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              type="submit"
              :icon="['fal', 'angle-right']"
              :disabled="!validator.passes"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'check']"
              theme="primary"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
    </div>
  </Loader>
</template>

<script>
import moment from 'moment'

import Model from '@/models/Project/base'
import ValidatorMixin from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'

import { mapState, mapActions } from 'vuex'

import { mapi18n } from '@/i18n'
import { mergeDeepRight } from 'ramda'
import Bus from '@/bus'
import { buildStepsFields } from '@/utils'

import { StepperComponent } from 'vue-elder-stepper'
import { DropdownComponent } from 'vue-elder-dropdown'
import HeaderComponent from '@/components/Header'
import { getValidatorConfig } from '@kvass/custom-fields'
import Steps from './Steps'
import Actions from '@/components/Actions'
import defaultFields from '@/components/Project/StepsFields/defaults.js'
import fields from '@/components/Project/StepsFields/fields.js'
import getMediaTypesFromTemplate from '@/utils/getMediaTypesFromTemplate'
import Draft from '@/components/Draft/Component'
import Preview from '@/components/Preview'
import DraftAlert from '@/components/Draft/Alert'
import { getExtensions } from '@/components/Tiptap/Extensions'
import FlatfinderImporter from '@/components/Flatfinder/Importer/Component.vue'

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'item',
      model: Model,
      guard: true,
      rules: [{ onKey: 'publishing.relation', compare: 'ref' }],
    }),
  ],
  props: {
    id: String,
  },
  provide() {
    return {
      RichTextOptions: {
        commands: getExtensions(),
        button: {
          internalItems: `/api/resource/post?field.label=title&field.value=id&field.thumbnail=media.cover&references=${this.id}&kvass-draft=true`,
          anchorItems: [
            { value: '#brochure', label: this.$t('brochure') },
            { value: '#lead', label: this.$t('leadForm') },
            { value: '#flatfinder', label: this.$tc('unitSelector', 1) },
          ],
          colors: ['primary', 'secondary', 'custom'],
          enableIcon: true,
        },
      },
    }
  },
  data() {
    return {
      stateKey: 'step',
      removeDialog: false,
      draft: true,
      key: 0,
      flatfinderImportPromise: null,
    }
  },
  watch: {
    id: {
      handler(id) {
        return this.fetch({ id }, { draft: this.draft })
      },
      immediate: true,
    },
    '$route.params.step': 'validateStep',
  },
  computed: {
    ...mapState('Project', ['item', 'promises', 'states']),
    types() {
      return getMediaTypesFromTemplate(this.$path('theme', this.item))
    },

    labels() {
      return {
        ...mapi18n({
          'media.logo': 'logo',
          'media.logoInverted': 'logoInverted',
          favicon: 'favicon',
          description: 'description',
          address: 'address',
          gallery: 'gallery',
          name: 'projectName',
          brochure: 'brochure',
          bidContract: 'bidContract',
          cover: 'cover',
          manager: 'projectmanager',
          salesman: 'customermanager',
          complaintmanager: 'complaintmanager',
          signingAuthority: 'signingAuthority',
          from: 'publishedFrom',
          to: 'publishedTo',
          attachments: ['attachment', 2],
          'publishing.domain': 'domain',
          'publishing.redirectUrl': 'redirectUrl',
        }),
        ...this.customFieldValidatorConfig.labels,
      }
    },

    rules() {
      let rules = {
        name: 'required',
        'publishing.domain': `domain|domainAvailable:${this.item.id}`,
        'publishing.redirectUrl': 'domain',
        ...this.customFieldValidatorConfig.rules,
      }

      let billingRules = {
        'billing.company': 'required',
        'billing.organizationNumber': 'required',
        'billing.email': 'required',
      }

      if (this.data && this.data.billing) return { ...rules, ...billingRules }
      return rules
    },

    hasActiveDraft() {
      return typeof this.draft === 'string'
    },

    showDraft() {
      return this.$path('item.metadata.draftAvailable') || this.hasActiveDraft
    },
    addons() {
      return this.$path('metadata.addons', this.item) || []
    },
    customActions() {
      return [
        {
          label: this.$tc('importUnitselector', 2),
          icon: 'fa-pro-light:arrow-down-to-dotted-line',
          category: this.$t('import'),
          onClick: ({ close }) => this.CreateFlatfindersFromFolders(close),
        },
      ]
    },
    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}

      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },
    customFieldSchemas() {
      if (!this.$path('item.customFieldsConfig')) return []
      return [
        ...(this.$path('item.customFieldsConfig') || [])
          .map(c => c.schema.map(e => ({ ...e, placement: c.placement || e.placement })))
          .flat(),
        ...[...Array.from(defaultFields.values() || []).map(i => i(this))],
      ]
    },
    breadcrumb() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$metaInfo.title,
          action: { name: 'project-dashboard', params: { id: this.id } },
          main: true,
        },
      ]
    },
    saveState() {
      let base = {
        save: {
          label: this.showDraft ? this.$t('saveDraft') : this.$t('save'),
          icon: 'arrow-to-bottom',
          disabled: !this.validator.passes || !this.hasChanges,
          theme: this.showDraft ? 'secondary' : 'primary',
        },
        publish: {
          label: this.$t('publish'),
          icon: 'chevron-down',
          disabled: !this.validator.passes || (!this.hasChanges && !this.draft),
          theme: 'secondary',
        },
      }

      if (!this.hasChanges) {
        base.save.tooltip = this.$t('noUnsavedChanges')

        if (!this.draft) {
          base.publish.tooltip = this.$t('noUnsavedChanges')
        }
      }

      if (!this.states.fetchOne && !this.validator.passes) {
        const invalid = {
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('<br>'),
        }

        Object.keys(base).forEach(key => {
          base[key] = { ...base[key], ...invalid }
        })
      }

      return base
    },

    steps() {
      const data = mergeDeepRight(this.item, { ...this.data, userRoles: this.$store.getters })
      let props = {
        value: this.data,
        data,
        item: this.item,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        customFieldSchemas: this.customFieldSchemas,
        fields: buildStepsFields(Array.from(fields.values()), this.customFieldSchemas, this),
      }

      let listeners = {
        submit: () => this.submit(),
        updateSchema: async () => {
          await this.$store.dispatch('Project/updateSchema')
        },
      }

      return Steps(data)
        .filter(e => {
          switch (e.id) {
            case 'shops':
              return (
                this.$store.getters['Project/hasRole'](/^shop/) &&
                !this.$store.state.enums.DisabledFeatures.includes('project:shop')
              )
            case 'sale':
              return !this.$store.state.enums.DisabledFeatures.includes('project:bid')
            default:
              return true
          }
        })
        .map(raw => {
          const { icon, ...s } = raw
          let errors = Object.keys(this.validator.errors.errors)
          s.isValid = (s.fields || []).every(f => !errors.find(e => e.startsWith(f)))
          s.props = {
            ...(s.props || {}),
            ...props,
            fields: {
              ...props.fields,
              default: props.fields.default.filter(i => i.defaultPlacement === raw.id),
            },
          }
          s.listeners = {
            ...(s.listeners || {}),
            ...listeners,
          }

          return s
        })
    },
    preventSubmit() {
      return !this.validator.passes || !this.hasChanges
    },
    hasActiveStep() {
      const step = this.$route.params[this.stateKey]
      return !step || this.steps.some(s => s.id === step)
    },
  },
  methods: {
    moment,
    ...mapActions('Project', {
      fetch(dispatch, ...rest) {
        return dispatch('fetch', rest).then(data => {
          if (!data) return this.$router.push({ name: 'notfound' })
          this.draft = data.draftId
          this.validateStep()
        })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),

    async submit() {
      if (this.preventSubmit) return Promise.resolve()

      const data = this.getData({ diff: true })
      const getConfig = 'theme' in data

      return this.mutate([
        {
          id: this.id,
          data,
          getConfig,
        },
        {
          strategy: (item, payload) => {
            const { metadata, customFieldsConfig, url, previewUrl, isPublished, themeLayout, draftId } = payload

            const merged = mergeDeepRight(item, {
              ...mergeDeepRight(this.data, {
                metadata,
                url,
                previewUrl,
                isPublished,
                draftId,
                ...(getConfig ? { customFieldsConfig, themeLayout } : {}),
              }),
            })

            if (metadata && metadata.disabledFields) {
              merged.metadata.disabledFields = metadata.disabledFields
            }

            return merged
          },
          transformPayload: false,
          draft: this.draft,
        },
      ]).then(res => {
        // set draft id after autosave
        if (this.draft === true && this.$path('draftId', res)) this.draft = this.$path('draftId', res)

        this.$bus.$emit('actions:refetch', 'project')
      })
    },
    CreateFlatfindersFromFolders(close) {
      close()
      Bus.emit('modal:show', {
        component: FlatfinderImporter,
        modalProps: { clickaway: true },
        props: { project: this.id },
      })
    },
    async complete() {
      if (!this.draft) this.draft = this.showDraft

      await this.submit()
      this.$router.push({ name: 'project-dashboard', params: { id: this.id } })
    },

    onRemoved() {
      this.resetData()
      this.$router.push({ name: 'projects' })
    },
    chat(value, tag) {
      Bus.$emit('chat', `${tag ? tag + ' - ' : ''}${this.$t('upsaleRequestTemplate', [value])} - ${this.item.name}`)
    },
    onNavigation(callback) {
      callback()
      if (this.preventSubmit) return
      // set draft so autosaves to draft
      if (!this.draft) this.draft = this.showDraft
      this.submit()
    },

    validateStep() {
      if (this.states.fetchOne || this.hasActiveStep) return
      this.$router.replace({
        params: { [this.stateKey]: this.steps[0].id },
        query: this.$route.query,
      })
    },
  },

  metaInfo() {
    return {
      title: this.item.name || `${this.$t('loading', { resource: this.$tc('project').toLowerCase() })}...`,
    }
  },

  created() {
    Bus.$on('project:refetch', () => {
      return this.fetch({ id: this.id }, { draft: true }).then(() => {
        this.key += 1
      })
    })
    Bus.$on('draft:update', val => {
      this.draft = val
    })
  },

  components: {
    HeaderComponent,
    StepperComponent,
    DropdownComponent,
    Actions,
    Draft,
    Preview,
    DraftAlert,
  },
}
</script>

<style lang="scss">
.project-mutate {
  background-color: $background-grey;
  display: flex;

  &__navigation {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
